import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";

import { downloadQuotationFile } from "./download";
import { generateQuotationDocx, generateQuotationPdf } from "./export";
import { LineItemEditor } from "./LineItemEditor";
import type {
  BillingCadence,
  GeneratedQuotationFile,
  Quotation,
  QuotationHandoffMode,
  QuotationParty,
  QuotationTemplateId,
  QuotationWorkspaceProps,
} from "./model";
import { QuotationPreview } from "./QuotationPreview";
import {
  deleteQuotationDraft,
  listQuotationDrafts,
  saveQuotationDraft,
} from "./repository";
import {
  createManualLineItem,
  createQuotationFromTemplate,
  switchSmartAttendanceToManualPricing,
} from "./templates";
import { calculateQuotationTotals } from "./totals";
import { validateQuotation } from "./validation";

import "./quotation.css";

type ExportFormat = "pdf" | "docx";
type PersistenceState =
  | { kind: "loading"; label: string }
  | { kind: "saving"; label: string }
  | { kind: "saved"; label: string }
  | { kind: "error"; label: string };

function formatSavedTime(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

function draftLabel(quotation: Quotation): string {
  const customer =
    quotation.customer.name.trim().length > 0
      ? quotation.customer.name
      : "Unnamed customer";
  const number =
    quotation.quotationNumber.trim().length > 0
      ? quotation.quotationNumber
      : "No number";
  return `${customer} - ${number}`;
}

export function QuotationWorkspace({ onSendFile }: QuotationWorkspaceProps) {
  const [quotation, setQuotation] = useState<Quotation>(() =>
    createQuotationFromTemplate("blank"),
  );
  const [drafts, setDrafts] = useState<Quotation[]>([]);
  const [persistence, setPersistence] = useState<PersistenceState>({
    kind: "loading",
    label: "Loading drafts on this device",
  });
  const [exportState, setExportState] = useState<{
    busy: boolean;
    label: string;
    error: string;
  }>({ busy: false, label: "", error: "" });

  const totals = useMemo(() => calculateQuotationTotals(quotation), [quotation]);
  const validation = useMemo(() => validateQuotation(quotation), [quotation]);

  const replaceQuotation = useCallback(
    (next: Quotation | ((current: Quotation) => Quotation)) => {
      setPersistence({
        kind: "saving",
        label: "Saving on this device",
      });
      setQuotation((current) => {
        const resolved = typeof next === "function" ? next(current) : next;
        return {
          ...resolved,
          updatedAt: new Date().toISOString(),
        };
      });
    },
    [],
  );

  useEffect(() => {
    let active = true;
    void listQuotationDrafts()
      .then((storedDrafts) => {
        if (active) {
          setDrafts(storedDrafts);
          setPersistence({
            kind: "saved",
            label:
              storedDrafts.length === 0
                ? "Drafts stay on this device"
                : `${storedDrafts.length} local draft${
                    storedDrafts.length === 1 ? "" : "s"
                  } found`,
          });
        }
      })
      .catch(() => {
        if (active) {
          setPersistence({
            kind: "error",
            label: "Local drafts are unavailable in this browser",
          });
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      void saveQuotationDraft(quotation)
        .then(() => {
          if (!active) {
            return;
          }
          setPersistence({
            kind: "saved",
            label: `Saved on this device at ${formatSavedTime(new Date())}`,
          });
          setDrafts((current) => {
            const withoutCurrent = current.filter((draft) => draft.id !== quotation.id);
            return [structuredClone(quotation), ...withoutCurrent].sort((left, right) =>
              right.updatedAt.localeCompare(left.updatedAt),
            );
          });
        })
        .catch(() => {
          if (active) {
            setPersistence({
              kind: "error",
              label: "Draft could not be saved; edits remain on this page",
            });
          }
        });
    }, 700);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [quotation]);

  const patchParty = (
    party: "seller" | "customer",
    field: keyof QuotationParty,
    value: string,
  ) => {
    replaceQuotation((current) => ({
      ...current,
      [party]: {
        ...current[party],
        [field]: value,
      },
    }));
  };

  const patchText =
    (
      field:
        | "quotationNumber"
        | "issueDate"
        | "validUntil"
        | "currency"
        | "title"
        | "introduction"
        | "preparedBy"
        | "closingNote",
    ) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      replaceQuotation((current) => ({
        ...current,
        [field]:
          field === "currency"
            ? event.target.value.toUpperCase().slice(0, 3)
            : event.target.value,
      }));
    };

  const addLineItem = (cadence: BillingCadence) => {
    replaceQuotation((current) => ({
      ...current,
      lineItems: [...current.lineItems, createManualLineItem(cadence)],
    }));
  };

  const chooseTemplate = (templateId: QuotationTemplateId) => {
    setPersistence({
      kind: "saving",
      label: "Saving on this device",
    });
    setQuotation(createQuotationFromTemplate(templateId));
    setExportState({ busy: false, label: "", error: "" });
  };

  const removeDraft = async (draft: Quotation) => {
    if (draft.id === quotation.id) {
      setQuotation(createQuotationFromTemplate("blank"));
    }

    setDrafts((current) => current.filter((candidate) => candidate.id !== draft.id));
    try {
      await deleteQuotationDraft(draft.id);
    } catch {
      setPersistence({
        kind: "error",
        label: "The local draft could not be deleted",
      });
    }
  };

  const generateFile = async (format: ExportFormat): Promise<GeneratedQuotationFile> =>
    format === "pdf"
      ? generateQuotationPdf(quotation)
      : generateQuotationDocx(quotation);

  const runExport = async (
    format: ExportFormat,
    destination: "download" | QuotationHandoffMode,
  ) => {
    setExportState({
      busy: true,
      label: `Generating ${format.toUpperCase()} locally`,
      error: "",
    });

    try {
      const file = await generateFile(format);
      if (destination === "download") {
        downloadQuotationFile(file);
        setExportState({
          busy: false,
          label: `${file.name} is ready to save`,
          error: "",
        });
      } else {
        onSendFile(
          {
            name: file.name,
            type: file.type,
            blob: file.blob,
          },
          destination,
        );
        setExportState({
          busy: false,
          label: `${file.name} handed to ${
            destination === "qr" ? "QR transfer" : "link sharing"
          }`,
          error: "",
        });
      }
    } catch (error: unknown) {
      setExportState({
        busy: false,
        label: "",
        error:
          error instanceof Error
            ? error.message
            : "The document could not be generated.",
      });
    }
  };

  const recurringItems = quotation.lineItems.filter(
    ({ billingCadence }) => billingCadence === "recurring",
  );
  const oneTimeItems = quotation.lineItems.filter(
    ({ billingCadence }) => billingCadence === "one-time",
  );

  return (
    <section
      className="quotation-workspace"
      aria-labelledby="quotation-workspace-heading"
    >
      <header className="quotation-workspace__header">
        <div>
          <span className="quotation-eyebrow">Quotation maker</span>
          <h2 id="quotation-workspace-heading">Create a customer-ready quotation</h2>
          <p>
            Draft, calculate, preview, and export locally. Nothing is uploaded while you
            edit or generate a document.
          </p>
        </div>
        <div
          className={`quotation-save-state quotation-save-state--${persistence.kind}`}
          role="status"
        >
          <span aria-hidden="true" />
          {persistence.label}
        </div>
      </header>

      <section
        className="quotation-template-picker"
        aria-labelledby="quotation-template-heading"
      >
        <div>
          <span className="quotation-eyebrow">Starting point</span>
          <h2 id="quotation-template-heading">Choose a template</h2>
          <p>Templates are editable starting content, never locked formats.</p>
        </div>
        <div className="quotation-template-picker__actions">
          <button
            type="button"
            className={`quotation-template-card${
              quotation.templateId === "blank" ? " quotation-template-card--active" : ""
            }`}
            onClick={() => {
              chooseTemplate("blank");
            }}
          >
            <strong>Blank quotation</strong>
            <span>Start with your own products and services.</span>
          </button>
          <button
            type="button"
            className={`quotation-template-card${
              quotation.templateId === "smart-attendance"
                ? " quotation-template-card--active"
                : ""
            }`}
            onClick={() => {
              chooseTemplate("smart-attendance");
            }}
          >
            <strong>Smart Attendance</strong>
            <span>Review-gated seed; no automatic price assumptions.</span>
          </button>
        </div>
      </section>

      {drafts.length > 0 ? (
        <details className="quotation-drafts">
          <summary>Drafts on this device ({drafts.length})</summary>
          <ul>
            {drafts.map((draft) => (
              <li key={draft.id}>
                <button
                  type="button"
                  className="quotation-draft-open"
                  onClick={() => {
                    setPersistence({
                      kind: "saving",
                      label: "Saving on this device",
                    });
                    setQuotation(structuredClone(draft));
                  }}
                  aria-current={draft.id === quotation.id ? "true" : undefined}
                >
                  <strong>{draftLabel(draft)}</strong>
                  <span>Updated {new Date(draft.updatedAt).toLocaleString()}</span>
                </button>
                <button
                  className="quotation-button quotation-button--quiet quotation-button--danger"
                  type="button"
                  onClick={() => {
                    void removeDraft(draft);
                  }}
                  aria-label={`Delete local draft ${draftLabel(draft)}`}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
          <p>
            Drafts use this browser&apos;s IndexedDB. They are not cloud backups and can
            be cleared by this browser or device.
          </p>
        </details>
      ) : null}

      <div className="quotation-workbench">
        <div className="quotation-editor">
          {quotation.templateContext.kind === "smart-attendance" ? (
            <section
              className={`quotation-pricing-review quotation-pricing-review--${quotation.templateContext.pricingMode}`}
              aria-labelledby="smart-pricing-heading"
            >
              <div>
                <span className="quotation-eyebrow">Pricing provenance</span>
                <h2 id="smart-pricing-heading">Smart Attendance pricing</h2>
              </div>
              <span className="quotation-pricing-review__status">
                {quotation.templateContext.pricingMode === "needs-review"
                  ? "Needs review"
                  : "Manual price"}
              </span>
              <div className="quotation-field">
                <label htmlFor="smart-employee-count">Employee count</label>
                <input
                  id="smart-employee-count"
                  type="number"
                  min={1}
                  step={1}
                  value={quotation.templateContext.employeeCount ?? ""}
                  onChange={(event) => {
                    const nextValue = event.target.valueAsNumber;
                    replaceQuotation((current) => {
                      if (current.templateContext.kind !== "smart-attendance") {
                        return current;
                      }
                      return {
                        ...current,
                        templateContext: {
                          ...current.templateContext,
                          employeeCount:
                            Number.isSafeInteger(nextValue) && nextValue > 0
                              ? nextValue
                              : null,
                        },
                      };
                    });
                  }}
                />
              </div>
              {quotation.templateContext.pricingMode === "needs-review" ? (
                <>
                  <p>
                    The seed workbook does not confirm tier boundaries or how the
                    face-registration amount should be calculated. Automatic pricing is
                    disabled, and export is blocked.
                  </p>
                  <button
                    className="quotation-button quotation-button--secondary"
                    type="button"
                    onClick={() => {
                      replaceQuotation((current) =>
                        switchSmartAttendanceToManualPricing(current),
                      );
                    }}
                  >
                    Switch to labeled manual pricing
                  </button>
                </>
              ) : (
                <p>
                  Manual pricing is active. Entered amounts are visibly labeled
                  <strong> Manual price</strong> in the editor and document.
                </p>
              )}
            </section>
          ) : null}

          <section className="quotation-editor-section">
            <div className="quotation-editor-section__heading">
              <span>01</span>
              <div>
                <h2>Business and customer</h2>
                <p>Identify who is issuing and receiving this quotation.</p>
              </div>
            </div>
            <div className="quotation-form-grid">
              <fieldset className="quotation-party-card">
                <legend>Seller</legend>
                <div className="quotation-field">
                  <label htmlFor="seller-name">Business name</label>
                  <input
                    id="seller-name"
                    value={quotation.seller.name}
                    onChange={(event) => {
                      patchParty("seller", "name", event.target.value);
                    }}
                    aria-invalid={
                      validation.errors.some(
                        ({ code }) => code === "SELLER_REQUIRED",
                      ) || undefined
                    }
                  />
                </div>
                <div className="quotation-field">
                  <label htmlFor="seller-address">Address (optional)</label>
                  <textarea
                    id="seller-address"
                    value={quotation.seller.address}
                    onChange={(event) => {
                      patchParty("seller", "address", event.target.value);
                    }}
                    rows={2}
                  />
                </div>
                <div className="quotation-field">
                  <label htmlFor="seller-email">Email (optional)</label>
                  <input
                    id="seller-email"
                    type="email"
                    value={quotation.seller.email}
                    onChange={(event) => {
                      patchParty("seller", "email", event.target.value);
                    }}
                  />
                </div>
                <div className="quotation-field">
                  <label htmlFor="seller-phone">Phone (optional)</label>
                  <input
                    id="seller-phone"
                    type="tel"
                    value={quotation.seller.phone}
                    onChange={(event) => {
                      patchParty("seller", "phone", event.target.value);
                    }}
                  />
                </div>
              </fieldset>

              <fieldset className="quotation-party-card">
                <legend>Customer</legend>
                <div className="quotation-field">
                  <label htmlFor="customer-name">Customer or company</label>
                  <input
                    id="customer-name"
                    value={quotation.customer.name}
                    onChange={(event) => {
                      patchParty("customer", "name", event.target.value);
                    }}
                    aria-invalid={
                      validation.errors.some(
                        ({ code }) => code === "CUSTOMER_REQUIRED",
                      ) || undefined
                    }
                  />
                </div>
                <div className="quotation-field">
                  <label htmlFor="customer-address">Address (optional)</label>
                  <textarea
                    id="customer-address"
                    value={quotation.customer.address}
                    onChange={(event) => {
                      patchParty("customer", "address", event.target.value);
                    }}
                    rows={2}
                  />
                </div>
                <div className="quotation-field">
                  <label htmlFor="customer-email">Email (optional)</label>
                  <input
                    id="customer-email"
                    type="email"
                    value={quotation.customer.email}
                    onChange={(event) => {
                      patchParty("customer", "email", event.target.value);
                    }}
                  />
                </div>
                <div className="quotation-field">
                  <label htmlFor="customer-phone">Phone (optional)</label>
                  <input
                    id="customer-phone"
                    type="tel"
                    value={quotation.customer.phone}
                    onChange={(event) => {
                      patchParty("customer", "phone", event.target.value);
                    }}
                  />
                </div>
              </fieldset>
            </div>
          </section>

          <section className="quotation-editor-section">
            <div className="quotation-editor-section__heading">
              <span>02</span>
              <div>
                <h2>Quotation details</h2>
                <p>Set the document identity, dates, currency, and purpose.</p>
              </div>
            </div>
            <div className="quotation-form-grid quotation-form-grid--compact">
              <div className="quotation-field">
                <label htmlFor="quotation-number">Quotation number</label>
                <input
                  id="quotation-number"
                  value={quotation.quotationNumber}
                  onChange={patchText("quotationNumber")}
                />
              </div>
              <div className="quotation-field">
                <label htmlFor="currency">Currency</label>
                <input
                  id="currency"
                  value={quotation.currency}
                  maxLength={3}
                  onChange={patchText("currency")}
                />
              </div>
              <div className="quotation-field">
                <label htmlFor="issue-date">Issue date</label>
                <input
                  id="issue-date"
                  type="date"
                  value={quotation.issueDate}
                  onChange={patchText("issueDate")}
                />
              </div>
              <div className="quotation-field">
                <label htmlFor="valid-until">Valid until</label>
                <input
                  id="valid-until"
                  type="date"
                  value={quotation.validUntil}
                  onChange={patchText("validUntil")}
                />
              </div>
              <div className="quotation-field quotation-field--wide">
                <label htmlFor="quotation-title">Quotation title</label>
                <input
                  id="quotation-title"
                  value={quotation.title}
                  onChange={patchText("title")}
                />
              </div>
              <div className="quotation-field quotation-field--wide">
                <label htmlFor="quotation-introduction">Introduction (optional)</label>
                <textarea
                  id="quotation-introduction"
                  value={quotation.introduction}
                  onChange={patchText("introduction")}
                  rows={3}
                />
              </div>
            </div>
          </section>

          <section className="quotation-editor-section" id="line-items">
            <div className="quotation-editor-section__heading">
              <span>03</span>
              <div>
                <h2>Items and pricing</h2>
                <p>Recurring and one-time charges remain separate in every output.</p>
              </div>
            </div>

            <div className="quotation-item-group">
              <div className="quotation-item-group__heading">
                <div>
                  <h3>Recurring charges</h3>
                  <p>Subscriptions or services charged on a regular cycle.</p>
                </div>
                <button
                  className="quotation-button quotation-button--secondary"
                  type="button"
                  onClick={() => {
                    addLineItem("recurring");
                  }}
                >
                  Add recurring item
                </button>
              </div>
              {recurringItems.length === 0 ? (
                <p className="quotation-empty-state">
                  No recurring charges in this draft.
                </p>
              ) : (
                recurringItems.map((lineItem) => (
                  <LineItemEditor
                    key={`${quotation.id}-${lineItem.id}`}
                    lineItem={lineItem}
                    currency={quotation.currency}
                    currencyMinorUnits={quotation.currencyMinorUnits}
                    onChange={(nextLineItem) => {
                      replaceQuotation((current) => ({
                        ...current,
                        lineItems: current.lineItems.map((candidate) =>
                          candidate.id === nextLineItem.id ? nextLineItem : candidate,
                        ),
                      }));
                    }}
                    onDelete={() => {
                      replaceQuotation((current) => ({
                        ...current,
                        lineItems: current.lineItems.filter(
                          ({ id }) => id !== lineItem.id,
                        ),
                      }));
                    }}
                  />
                ))
              )}
            </div>

            <div className="quotation-item-group">
              <div className="quotation-item-group__heading">
                <div>
                  <h3>One-time charges</h3>
                  <p>Products, setup, installation, delivery, or other fees.</p>
                </div>
                <button
                  className="quotation-button quotation-button--secondary"
                  type="button"
                  onClick={() => {
                    addLineItem("one-time");
                  }}
                >
                  Add one-time item
                </button>
              </div>
              {oneTimeItems.length === 0 ? (
                <p className="quotation-empty-state">
                  No one-time charges in this draft.
                </p>
              ) : (
                oneTimeItems.map((lineItem) => (
                  <LineItemEditor
                    key={`${quotation.id}-${lineItem.id}`}
                    lineItem={lineItem}
                    currency={quotation.currency}
                    currencyMinorUnits={quotation.currencyMinorUnits}
                    onChange={(nextLineItem) => {
                      replaceQuotation((current) => ({
                        ...current,
                        lineItems: current.lineItems.map((candidate) =>
                          candidate.id === nextLineItem.id ? nextLineItem : candidate,
                        ),
                      }));
                    }}
                    onDelete={() => {
                      replaceQuotation((current) => ({
                        ...current,
                        lineItems: current.lineItems.filter(
                          ({ id }) => id !== lineItem.id,
                        ),
                      }));
                    }}
                  />
                ))
              )}
            </div>
          </section>

          <section className="quotation-editor-section">
            <div className="quotation-editor-section__heading">
              <span>04</span>
              <div>
                <h2>Terms and closing</h2>
                <p>Only include conditions you intentionally enter.</p>
              </div>
            </div>
            <div className="quotation-form-grid">
              <div className="quotation-field quotation-field--wide">
                <label htmlFor="quotation-terms">Terms (one condition per line)</label>
                <textarea
                  id="quotation-terms"
                  rows={5}
                  value={quotation.terms.join("\n")}
                  onChange={(event) => {
                    replaceQuotation((current) => ({
                      ...current,
                      terms: event.target.value.split("\n"),
                    }));
                  }}
                />
              </div>
              <div className="quotation-field">
                <label htmlFor="prepared-by">Prepared by (optional)</label>
                <input
                  id="prepared-by"
                  value={quotation.preparedBy}
                  onChange={patchText("preparedBy")}
                />
              </div>
              <div className="quotation-field quotation-field--wide">
                <label htmlFor="closing-note">Closing note (optional)</label>
                <textarea
                  id="closing-note"
                  rows={3}
                  value={quotation.closingNote}
                  onChange={patchText("closingNote")}
                />
              </div>
            </div>
          </section>

          <section
            className="quotation-validation"
            aria-labelledby="quotation-validation-heading"
          >
            <div>
              <span className="quotation-eyebrow">Review</span>
              <h2 id="quotation-validation-heading">
                {validation.canExport
                  ? "Ready to export"
                  : `${validation.errors.length} item${
                      validation.errors.length === 1 ? "" : "s"
                    } need attention`}
              </h2>
            </div>
            {validation.messages.length > 0 ? (
              <ul>
                {validation.messages.map((validationMessage, index) => (
                  <li
                    key={`${validationMessage.code}-${validationMessage.field}-${index}`}
                    className={`quotation-validation__${validationMessage.severity}`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const target = document.getElementById(validationMessage.field);
                        target?.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                        if (
                          target instanceof HTMLInputElement ||
                          target instanceof HTMLTextAreaElement ||
                          target instanceof HTMLButtonElement ||
                          target instanceof HTMLSelectElement
                        ) {
                          target.focus({ preventScroll: true });
                        }
                      }}
                    >
                      {validationMessage.message}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>All required information and authoritative prices are ready.</p>
            )}
          </section>

          <section
            className="quotation-export-panel"
            aria-labelledby="quotation-export-heading"
          >
            <div>
              <span className="quotation-eyebrow">Local document export</span>
              <h2 id="quotation-export-heading">Save or hand off a file</h2>
              <p>
                PDF and DOCX are generated in this browser from the same validated
                snapshot.
              </p>
            </div>

            <div className="quotation-export-grid">
              {(["pdf", "docx"] as const).map((format) => (
                <article className="quotation-export-card" key={format}>
                  <div>
                    <strong>{format.toUpperCase()}</strong>
                    <span>
                      {format === "pdf"
                        ? "Fixed customer-ready layout"
                        : "Editable Word-compatible document"}
                    </span>
                  </div>
                  <button
                    className="quotation-button quotation-button--primary"
                    type="button"
                    disabled={!validation.canExport || exportState.busy}
                    onClick={() => {
                      void runExport(format, "download");
                    }}
                  >
                    Download {format.toUpperCase()}
                  </button>
                  <div className="quotation-export-card__handoff">
                    <button
                      className="quotation-button quotation-button--secondary"
                      type="button"
                      disabled={!validation.canExport || exportState.busy}
                      onClick={() => {
                        void runExport(format, "qr");
                      }}
                    >
                      Send by QR
                    </button>
                    <button
                      className="quotation-button quotation-button--secondary"
                      type="button"
                      disabled={!validation.canExport || exportState.busy}
                      onClick={() => {
                        void runExport(format, "link");
                      }}
                    >
                      Send via link
                    </button>
                  </div>
                </article>
              ))}
            </div>

            <div
              className={`quotation-export-status${
                exportState.error.length > 0 ? " quotation-export-status--error" : ""
              }`}
              role="status"
              aria-live="polite"
            >
              {exportState.error || exportState.label}
            </div>
          </section>
        </div>

        <aside className="quotation-preview-column">
          <QuotationPreview quotation={quotation} totals={totals} />
        </aside>
      </div>
    </section>
  );
}
